import { api } from '@/shared/axios-client';
import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';

// ── Response types (from API_RESPONSE.txt) ─────────────────────────────────

export interface CoverageSummaryMetric {
  percentage?: number;
  covered?: number;
  total?: number;
}

export interface CoverageSummary {
  totalCoverage?: CoverageSummaryMetric;
  testAutomation?: CoverageSummaryMetric & { testCount: number };
  manualTests?: CoverageSummaryMetric & { testCount: number };
  uncovered?: { percentage?: number; uncovered?: number; total?: number };
}

export interface EntityDetails {
  entityName: string;
  entityType: string;
  entityKey: string;
  entityId: string;
  is_ignored: boolean;
  startLine: number;
  endLine: number;
  totalExecutableLines: number;
  nonExecutableLinesCount: number;
  coveredExecutableLines: number;
  uncoveredExecutableLinesCount: number;
  locCoverage: {
    totalCoverage: number;
    totalExecutableLinesCount: number;
    coveredLinesCount: number;
    uncoveredLinesCount: number;
  };
  inventoryCoverage: {
    totalCoverage: number;
    covered: number;
    total: number;
    childrenCovered: number;
    childrenTotal: number;
  };
}

export interface EntityDetailsInventory {
  entityName: string;
  entityType: string;
    entityKey: string;
  entityId: string;
  is_ignored: boolean;
  startLine: number;
  endLine: number;
  totalExecutableLines: number;
  nonExecutableLinesCount: number;
  coveredExecutableLines: number;
  uncoveredExecutableLinesCount: number;
  inventoryCoverage: {
    totalCoverage: number;
    covered: number;
    total: number;
    childrenCovered: number;
    childrenTotal: number;
  };
}

export interface LinesCoverageFile {
  filePath: string;
  fileName: string;
  totalCoverage: number;
  totalExecutableLines: number;
  coveredLinesCount: number;
  uncoveredLines: number;
  entityDetails: EntityDetails[];
}
export interface CodeElementMetric {
  total?: number;
  covered?: number;
  percentage?: number;
}

export interface CoverageByCodeElement {
  classes?: CodeElementMetric;
  methods?: CodeElementMetric;
  branches?: CodeElementMetric;
  functions?: CodeElementMetric;
  loops?: CodeElementMetric;
}

export interface InventoryCoverageFile {
  filePath: string;
  fileName: string;
  totalCoverage: number;
  classes: { covered: number; total: number };
  methods: { covered: number; total: number };
  branches: { covered: number; total: number };
  loops: { covered: number; total: number };
  functions: { covered: number; total: number };
  totalElements: number;
  coveredElements: number;
  uncoveredElements: number;
  coveredBy: { auto: number; manual: number };
  entityDetails: EntityDetailsInventory[];
}

export interface PollerState {
  nextScheduledPoll: string;
  lastTestPolledTime: string;
  lastPolledTime: string;
  lastCodePolledTime: string;
  interval: number;
  createdAt: string;
  updatedAt: string;
}

export interface CoverageAggregateData {
  scanId: string;
  isFrontend: boolean;
  linesOfCodeMode: {
    summary: CoverageSummary;
    fileLevelCoverage: LinesCoverageFile[];
  };

  inventoryMode: {
    summary: CoverageSummary;
    coverageByCodeElement: CoverageByCodeElement;
    fileLevelCoverage: InventoryCoverageFile[];
  };
  pagination: {
    totalFiles: number;
    offset: number;
    limit: number;
    returnedFiles: number;
  };
  coverageAutomation: {
    linesOfCodeMode: {
      summary: CoverageSummary;
      fileLevelCoverage: LinesCoverageFile[];
    };
    inventoryMode: {
      summary: CoverageSummary;
      coverageByCodeElement: CoverageByCodeElement;
      fileLevelCoverage: InventoryCoverageFile[];
    };
  };
  coverageManual: {
    linesOfCodeMode: {
      summary: CoverageSummary;
      fileLevelCoverage: LinesCoverageFile[];
    };
    inventoryMode: {
      summary: CoverageSummary;
      coverageByCodeElement: CoverageByCodeElement;
      fileLevelCoverage: InventoryCoverageFile[];
    };
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  otherRepos: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  coverage: any;
  pollerState?: PollerState;
}

export interface CoverageAggregateResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: CoverageAggregateData;
}

// ── API call ───────────────────────────────────────────────────────────────

/** Page size for coverage file tables (must match backend default). */
export const COVERAGE_FILES_PAGE_SIZE = 50;

const getCoverageAggregate = (
  scanId: string,
  projectId: string,
  offset: number = 0,
  limit: number = COVERAGE_FILES_PAGE_SIZE,
): Promise<CoverageAggregateResponse> =>
  api.get('/api/coverage/aggregate/automation', {
    params: { scanId, projectId, offset, limit },
  });

// ── Query factory + hook ───────────────────────────────────────────────────

export const coverageAggregateQueryOptions = (
  scanId: string,
  projectId: string,
  offset: number = 0,
) =>
  queryOptions({
    queryKey: [
      'coverage-aggregate-automation',
      scanId,
      projectId,
      offset,
      COVERAGE_FILES_PAGE_SIZE,
    ],
    queryFn: () => getCoverageAggregate(scanId, projectId, offset),
    // projectId is optional (GitHub/Jenkins repos often have none); only needed for related-repo cards.
    enabled: Boolean(scanId),
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
  });

export const useCoverageAggregateAutomation = (
  scanId: string,
  projectId: string,
  offset: number = 0,
) => useQuery(coverageAggregateQueryOptions(scanId, projectId, offset));
